import React from 'react';
import {
  Box,
  Card,
  CardBody,
  Flex,
  Grid,
  Link,
  Text,
  TextField,
  HeaderPage,
  Skeleton,
} from '@backstage/ui';
import { RiSearchLine } from '@remixicon/react';
import { Content } from '@backstage/core-components';
import { attachComponentData, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { appTranslationRef } from '../../core/i18n';
import { EmptyState } from '../../shared/components';
import { getKindIcon, getKindColor } from '../../shared/constants';
import styles from './docs.page.module.css';

const DocsContent = ({
  loading,
  filteredEntities,
  ownerDisplay,
}: {
  loading: boolean;
  filteredEntities: Entity[];
  ownerDisplay: (owner: string) => React.JSX.Element;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Grid.Root columns="12" gap="4">
        {[0, 1, 2].map(i => (
          <Grid.Item key={i} colSpan={{ xs: '12', md: '4' }}>
            <Card className={styles.card}>
              <CardBody>
                <Flex direction="column" gap="2">
                  <Flex gap="2" align="center">
                    <Skeleton width={20} height={20} rounded />
                    <Skeleton width="60%" height={20} />
                  </Flex>
                  <Skeleton width="40%" height={16} />
                  <Skeleton width="100%" height={48} />
                </Flex>
              </CardBody>
            </Card>
          </Grid.Item>
        ))}
      </Grid.Root>
    );
  }
  if (filteredEntities.length === 0) {
    return (
      <EmptyState
        title={t('docs.emptyState.title')}
        description={t('docs.emptyState.description')}
      />
    );
  }
  return (
    <Grid.Root columns="12" gap="4">
      {filteredEntities.map(entity => {
        const namespace = entity.metadata.namespace || 'default';
        const kind = entity.kind.toLowerCase();
        const name = entity.metadata.name;
        const title = entity.metadata.title || entity.metadata.name;
        const owner = (entity.spec?.owner as string) || '';
        const description = entity.metadata.description || '';
        const Icon = getKindIcon(entity.kind);
        const iconColor = getKindColor(entity.kind);

        return (
          <Grid.Item
            key={`${namespace}/${kind}/${name}`}
            colSpan={{ xs: '12', md: '4' }}
          >
            <Link
              href={`/docs/${namespace}/${kind}/${name}`}
              className={styles.cardLink}
            >
              <Card className={styles.card}>
                <CardBody>
                  <Flex direction="column" gap="2">
                    <Flex gap="2" align="center">
                      <Box className={styles.iconColor} style={{ color: iconColor }}>
                        <Icon size={20} />
                      </Box>
                      <Text variant="title-small" weight="bold">
                        {title}
                      </Text>
                    </Flex>
                    {owner && ownerDisplay(owner)}
                    {description && (
                      <Text
                        variant="body-small"
                        color="secondary"
                        className={styles.description}
                      >
                        {description}
                      </Text>
                    )}
                  </Flex>
                </CardBody>
              </Card>
            </Link>
          </Grid.Item>
        );
      })}
    </Grid.Root>
  );
};

const DocsPageComponent = () => {
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const [entities, setEntities] = React.useState<Entity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchText, setSearchText] = React.useState('');

  React.useEffect(() => {
    catalogApi
      .getEntities({
        filter: {
          'metadata.annotations.backstage.io/techdocs-ref':
            CATALOG_FILTER_EXISTS,
        },
      })
      .then(({ items }) => {
        setEntities(items);
        setLoading(false);
      });
  }, [catalogApi]);

  const filteredEntities = React.useMemo(() => {
    if (!searchText) return entities;
    const lower = searchText.toLowerCase();
    return entities.filter(
      e =>
        e.metadata.name.toLowerCase().includes(lower) ||
        e.metadata.title?.toLowerCase().includes(lower) ||
        e.metadata.description?.toLowerCase().includes(lower) ||
        (e.spec?.owner as string)?.toLowerCase().includes(lower),
    );
  }, [entities, searchText]);

  const ownerDisplay = (owner: string) => {
    const stripped = owner.replace(/^group:default\//, '');
    const isGroup = owner.startsWith('group:default/');
    if (isGroup) {
      return (
        <Link
          href={`/catalog/default/group/${stripped}`}
          className={styles.ownerLink}
          onClick={e => e.stopPropagation()}
        >
          <Text variant="body-small" color="secondary">
            {stripped}
          </Text>
        </Link>
      );
    }
    return (
      <Text variant="body-small" color="secondary">
        {owner}
      </Text>
    );
  };

  return (
    <Box className={styles.pageShell}>
      <HeaderPage title={t('docs.pageTitle')} />
      <Content stretch>
        <Flex direction="column" gap="4">
          <Flex gap="4" align="center">
            <Box className={styles.searchWrapper}>
              <TextField
                className={styles.searchInput}
                placeholder={t('docs.searchPlaceholder')}
                value={searchText}
                onChange={setSearchText}
                icon={<RiSearchLine size={18} />}
              />
            </Box>
            {!loading && (
              <Text variant="body-small" color="secondary">
                {filteredEntities.length === 1
                  ? t('docs.entityCountSingular')
                  : t('docs.entityCountPlural').replace(
                      '{{count}}',
                      String(filteredEntities.length),
                    )}
              </Text>
            )}
          </Flex>

          <DocsContent
            loading={loading}
            filteredEntities={filteredEntities}
            ownerDisplay={ownerDisplay}
          />
        </Flex>
      </Content>
    </Box>
  );
};

// Bind the techdocs index routeRef so TechDocsReaderPage can resolve the "back to docs" link
attachComponentData(DocsPageComponent, 'core.mountPoint', techdocsPlugin.routes.root);

export const DocsPage = DocsPageComponent;
